/**
 * US-176 Phase 2 — a stable, per-device identifier. Referenced throughout
 * the backend/Lambda (`employee_reauth_credentials.device_id`, the
 * DeviceTrust `x-device-id` header, the CUSTOM_AUTH challengeAnswer) but
 * never actually defined client-side until now.
 *
 * Not a secret — it's an opaque label distinguishing "this physical
 * device" from another, the same role a browser fingerprint or install ID
 * plays elsewhere. localStorage is the right store for it (unlike the
 * device's actual credential, which is Keychain-only): losing it on
 * uninstall/reinstall just means the next full sign-in mints a "new"
 * device as far as the server is concerned, which is the correct outcome
 * anyway (the old credential's `device_id` no longer has a matching
 * private key).
 */
const DEVICE_ID_KEY = "ls.deviceId";

export function getOrCreateDeviceId(): string {
  try {
    const existing = localStorage.getItem(DEVICE_ID_KEY);
    if (existing) return existing;
    const id = crypto.randomUUID();
    localStorage.setItem(DEVICE_ID_KEY, id);
    return id;
  } catch {
    // Storage unavailable (private browsing, quota) — fall back to a
    // session-lived id rather than throwing; the caller's flow degrades to
    // "every attempt looks like a new device" rather than crashing.
    return crypto.randomUUID();
  }
}
